"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CloudConfigSchema = void 0;
const zod_1 = require("zod");
const rate_limits_1 = require("./rate-limits");
const plans_1 = require("../features/entitlements/plans");
exports.CloudConfigSchema = zod_1.z.object({
    plan: zod_1.z.enum(plans_1.cloudConfigPlans).optional(),
    monthlyObservationLimit: zod_1.z.number().int().positive().optional(),
    // used for table and dashboard queries
    defaultLookBackDays: zod_1.z.number().int().positive().optional(),
    // need to update stripe webhook if you change this, it fetches from db via these fields
    stripe: zod_1.z
        .object({
        customerId: zod_1.z.string().nullish(),
        activeSubscriptionId: zod_1.z.string().nullish(),
        activeProductId: zod_1.z.string().nullish(),
        activeUsageProductId: zod_1.z.string().nullish(),
        subscriptionStatus: zod_1.z.string().nullish(), // should be one of ["active","past_due", "unpaid", "canceled", "incomplete", "incomplete_expired", "paused"]; we don't enforce to have a backwards compatibility for this field
    })
        .transform((data) => ({
        ...data,
        isLegacySubscription: data?.activeProductId != null && data?.activeUsageProductId == null,
    }))
        .nullish(),
    // ClickHouse Billing (CHB) state. Written only by the CHB webhook handler,
    // except organizationId which checkout-session creation persists. Invariant:
    // an org carries either `stripe` billing state or `clickhouse` state, never
    // both — provider routing (getBillingProvider) and the worker jobs'
    // Stripe-customer-id selection depend on it.
    clickhouse: zod_1.z
        .object({
        // ClickHouse Organization ID. Required and uuid-validated: parseDbOrg
        // nulls the whole cloudConfig on parse failure, so strictness here is
        // only safe because both writers (checkout response, webhook handler)
        // validate the uuid before persisting. Change the stored schema first,
        // writers second, if the id format ever loosens.
        organizationId: zod_1.z.uuid(),
        // CHB attached plan id (`data.id` on webhook events, `id` on GET /attachedplan)
        attachedPlanId: zod_1.z.string().nullish(),
        // Validated against the shared plan-code enum so downstream resolution is
        // an exhaustive lookup. `.catch(null)` contains the damage if a code ever
        // drifts (CHB renames a tier, or ships one before we deploy support for
        // it): parseDbOrg nulls the *entire* cloudConfig on a parse failure, which
        // would also discard the org's plan override, rate-limit overrides and
        // stripe.customerId — and an org without a Stripe customer id drops out of
        // the usage-metering job's selection and silently stops being billed.
        // Unknown codes are rejected at the webhook so they are never stored.
        planCode: zod_1.z.enum(plans_1.chbPlanCodes).nullish().catch(null),
        paymentStatus: zod_1.z.string().nullish(), // "active" | "failed" | ...
        nextPaymentDate: zod_1.z.string().nullish(),
        // Stripe customer behind the CHB bundle (payment.provider.customerId on
        // attachedplan.* events). Support tooling only — routing, plan resolution, and
        // the worker jobs never read it.
        stripeCustomerId: zod_1.z.string().nullish(),
        // Snapshot of a pending scheduled change (downgrade/cancel) for the UI.
        scheduled: zod_1.z
            .object({
            type: zod_1.z.string(), // "upgrade" | "downgrade" | "cancel"
            when: zod_1.z.string(), // "immediate" | "billing_cycle_end" | ISO date
            planCode: zod_1.z.string().nullish(),
            startDate: zod_1.z.string().nullish(),
        })
            .nullish(),
        // Monotonic guard against out-of-order webhook delivery.
        lastEventCreatedAt: zod_1.z.string().nullish(),
    })
        .nullish(),
    // custom rate limits for an organization
    rateLimitOverrides: rate_limits_1.CloudConfigRateLimit.optional(),
});
